import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import DigitalConsciousnessIcon from "./DigitalConsciousnessIcon";
import { sendChatMessage } from "../../utils/api";
import { useMedia } from "react-use";
const quickChips = [
  "Design Experience",
  "Case Studies",
  "Process & Tools",
  "My Visa Status",
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Chatbot() {
  const isMobileSize = useMedia("(max-width: 480px)");
  const [query, setQuery] = useState("");
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleChipClick = (chip) => {
    setQuery(chip);
    inputRef.current?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!query.trim() || isLoading) return;

    const userMessage = {
      id: Date.now().toString(),
      type: "user",
      content: {
        text: query,
        sources: [],
      },
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);
    const currentQuery = query;
    setQuery("");

    try {
      const response = await sendChatMessage(currentQuery);
      if (response && response.reply) {
        let data = null;
        try {
          data = JSON.parse(response.reply);
        } catch (error) {
          const text =
            response.reply ||
            "Sorry, something went wrong while fetching the response. Please again.";
          data = { text: text, links: [] };
          console.warn("Response is not valid JSON string", error);
        }
        console.log("API response data:", data);
        const aiMessage = {
          id: (Date.now() + 1).toString(),
          type: "ai",
          content: {
            text: data.text,
            sources: data.links || [],
          },
          timestamp: response.timestamp,
        };
        setMessages((prev) => [...prev, aiMessage]);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        type: "ai",
        content: {
          text: "Sorry, something went wrong while fetching the response. Please try again.",
          sources: [],
        },
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen d-flex flex-column text-white"
      style={{ backgroundColor: "#030303" }}
    >
      {messages.length === 0 ? (
        <div className="container py-5 d-flex flex-column gap-4">
          <div>
            {/* Sphere */}
            <motion.div
              custom={0}
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              className="relative flex items-center justify-center"
            >
              <DigitalConsciousnessIcon />
            </motion.div>

            {/* Headline */}
            <motion.div
              custom={1}
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              className="text-center text-md-center"
            >
              <h1 className="display-6 fw-semibold text-white mb-0">
                Meet <span className="Nithin-ai fw-bold">Nithin AI</span>, your
                guide to my design journey.
              </h1>
            </motion.div>

            {/* Subtitle */}
            <motion.p
              custom={2}
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              className="mt-3 text-center text-md-center fs-5 text-white-50"
            >
              Ask about my projects, process, or philosophy.
            </motion.p>
          </div>

          <div>
            {/* Quick chips */}
            <motion.div
              custom={3.5}
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              className="d-flex flex-wrap justify-content-center gap-3"
            >
              {quickChips.map((chip) => (
                <button
                  key={chip}
                  onClick={() => handleChipClick(chip)}
                  className="quick-chip btn rounded-pill px-4 py-2 fw-semibold"
                >
                  {chip}
                </button>
              ))}
            </motion.div>

            <motion.div
              custom={3.5}
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              className="w-100 flex-shrink-0"
            >
              <div
                className="container py-3"
                style={
                  isMobileSize ? { maxWidth: "100%" } : { maxWidth: "65%" }
                }
              >
                <motion.form
                  custom={0}
                  variants={fadeUp}
                  initial="hidden"
                  animate="visible"
                  onSubmit={handleSubmit}
                  className="rounded-pill border border-secondary bg-dark bg-opacity-75 shadow-sm"
                >
                  <div className="d-flex align-items-center justify-content-between px-4 py-2">
                    <div className="d-flex align-items-center gap-3 flex-grow-1">
                      <img
                        src="/assets/imgs/chatbot_star.png"
                        alt="spark"
                        className="me-2"
                        style={{ width: "24px", height: "24px" }}
                      />
                      <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Ask Nithin"
                        disabled={isLoading}
                        className="form-control bg-transparent border-0 text-white placeholder-white-50"
                        style={{ boxShadow: "none", outline: "none" }}
                        onFocus={(e) => {
                          e.target.style.boxShadow = "none";
                          e.target.style.outline = "none";
                        }}
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="btn btn-link text-primary p-0 ms-3"
                      aria-label="Send"
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5 12H19"
                          stroke="#2653FB"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M12 5L19 12L12 19"
                          stroke="#2653FB"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </motion.form>
              </div>
            </motion.div>

            {/* Footer links */}
            <motion.div
              custom={5.5}
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              className="d-flex justify-content-center gap-4"
            >
              <Link to="/home" className="text-white text-decoration-none">
                View full portfolio
              </Link>

              <a
                href="/assets/pdf/resume/Nithin Chandra Namani Resume.pdf"
                target="_blank"
                className="text-white text-decoration-none"
                rel="noreferrer"
              >
                Download Resume
              </a>
            </motion.div>
          </div>
        </div>
      ) : (
        /* Chat view - messages */
        <div className="flex-grow-1 px-4 pt-5 pb-5">
          <div className="container chat-container">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`pt-4 d-flex align-items-end ${message.type === "user" ? "justify-content-end" : "justify-content-start"}`}
              >
                {message.type === "ai" ? (
                  <div className="w-100" style={{ maxWidth: "80%" }}>
                    <div className="d-flex align-items-end align-items-md-start gap-3">
                      <div className="ai-avatar-outer">
                        <div className="ai-avatar-innter"></div>
                      </div>
                      <div className="ai-card">
                        <div className="small text-white-75 lh-lg">
                          <p>{message.content.text}</p>
                        </div>
                      </div>
                    </div>
                    {/* LINKS SECTION */}
                    {message.content.sources &&
                      message.content.sources.length > 0 && (
                        <div className="d-flex flex-wrap gap-2 mt-3 ps-14">
                          {message.content.sources.map((link, index) => (
                            <a
                              key={index}
                              href={link.url}
                              target="_blank"
                              rel="noreferrer"
                              className="badge rounded-pill bg-secondary text-white text-decoration-none px-3 py-2"
                            >
                              {link.label}
                            </a>
                          ))}
                        </div>
                      )}
                  </div>
                ) : (
                  <div
                    className="d-flex align-items-end align-items-md-start gap-3"
                    style={{ maxWidth: "80%" }}
                  >
                    <div className="user-card">
                      <div className="small lh-lg">
                        <p className="text-white">{message.content.text}</p>
                      </div>
                    </div>
                    <div className="user-avatar">
                      <span className="small text-white-75 fw-semibold">
                        You
                      </span>
                    </div>
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>
            ))}
            {isLoading && (
              <div className="d-flex gap-3 align-items-center pt-3">
                <div className="ai-avatar-outer">
                  <div className="ai-avatar-innter"></div>
                </div>
                <div className="d-flex justify-content-start">
                  <div className="ai-card">
                    <div className="d-flex gap-1 py-2">
                      <div
                        className="rounded-circle loader-color ab"
                        style={{ width: "0.5rem", height: "0.5rem" }}
                      ></div>
                      <div
                        className="rounded-circle loader-color ab1"
                        style={{
                          width: "0.5rem",
                          height: "0.5rem",
                        }}
                      ></div>
                      <div
                        className="rounded-circle loader-color ab2"
                        style={{
                          width: "0.5rem",
                          height: "0.5rem",
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="fixed bottom-2 bottom-md-4 start-0 w-100">
            <div
              className="container py-3"
              style={isMobileSize ? { maxWidth: "100%" } : { maxWidth: "50%" }}
            >
              <motion.form
                custom={0}
                variants={fadeUp}
                initial="hidden"
                animate="visible"
                onSubmit={handleSubmit}
                className="chat-input bg-dark bg-opacity-75"
              >
                <div className="d-flex align-items-center justify-content-between px-4 py-2">
                  <div className="d-flex align-items-center gap-3 flex-grow-1">
                    <img
                      src="/assets/imgs/chatbot_star.png"
                      alt="spark"
                      className="me-2"
                      style={{ width: "24px", height: "24px" }}
                    />
                    <input
                      ref={inputRef}
                      type="text"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Ask Nithin"
                      disabled={isLoading}
                      className="form-control bg-transparent border-0 text-white placeholder-white-50"
                      style={{ boxShadow: "none", outline: "none" }}
                      onFocus={(e) => {
                        e.target.style.boxShadow = "none";
                        e.target.style.outline = "none";
                      }}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="btn btn-link text-primary p-0 ms-3"
                    aria-label="Send"
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5 12H19"
                        stroke="#2653FB"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 5L19 12L12 19"
                        stroke="#2653FB"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </motion.form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
